import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import {
  CobaltFileUploader,
  type FileItem,
} from '@cae/cobalt-react/components/file-uploader';
import { CobaltModal } from '@cae/cobalt-react/components/modal';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import * as React from 'react';

import type { UploadApprovalLetterModalProps } from './UploadApprovalLetterModal.types';

import styles from './UploadApprovalLetterModal.module.css';
import {
  APPROVAL_LETTER_CONSTRAINTS_DESCRIPTION,
  APPROVAL_LETTER_FILE_EXTENSIONS,
  APPROVAL_LETTER_MAX_FILE_SIZE_KB,
  getApprovalLetterInvalidText,
} from './UploadApprovalLetterModal.utils';
import { UploadedFileItem } from './uploaded-file-item/UploadedFileItem';

const UploadApprovalLetterModal = React.memo(
  ({
    isOpen,
    displayName,
    isPending,
    uploadError,
    onClose,
    onUpload,
    onFileChange,
  }: Readonly<UploadApprovalLetterModalProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const [files, setFiles] = React.useState<FileItem[]>([]);
    const abortControllerRef = React.useRef<AbortController | null>(null);

    React.useEffect(() => {
      if (!isOpen) {
        setFiles([]);
        abortControllerRef.current = null;
      }
    }, [isOpen]);

    const currentFile = files[0] ?? null;
    const hasValidFile = currentFile !== null && !currentFile.invalid;
    const isUploadDisabled = !hasValidFile || isPending;

    const handleClose = React.useCallback((): void => {
      setFiles([]);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      onClose();
    }, [onClose]);

    const handleFilesChange = React.useCallback(
      (updatedFiles: FileItem[]): void => {
        setFiles(
          updatedFiles.map(f => {
            const invalidText = getApprovalLetterInvalidText(f.file);
            const isInvalid = invalidText !== '';
            return { ...f, invalid: isInvalid, invalidText };
          })
        );
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        onFileChange?.();
      },
      [onFileChange]
    );

    const handleRemoveFile = React.useCallback((): void => {
      setFiles([]);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      onFileChange?.();
    }, [onFileChange]);

    const handleUpload = React.useCallback((): void => {
      if (currentFile !== null && !currentFile.invalid) {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        onUpload(currentFile.file, controller.signal);
      }
    }, [currentFile, onUpload]);

    const uploaderLabel = (
      <p
        className={`body-02 ${styles.uploaderFor}`}
        data-testid="uploading-for-label"
      >
        Uploading for{' '}
        <span className={styles.semibold} data-testid="uploading-for-name">
          {displayName}
        </span>
      </p>
    );

    return (
      <CobaltModal
        data-testid="upload-approval-letter-modal"
        className={styles.modal}
        title="Upload approval letter"
        isOpen={isOpen}
        size={isMobileOrTablet ? 'large' : 'medium'}
        onClose={handleClose}
        actionBar={
          <CobaltActionBar
            variant="flat"
            position="right"
            primaryButton={
              <CobaltButton
                variant="primary"
                disabled={isUploadDisabled}
                pending={isPending}
                onClick={handleUpload}
                data-testid="upload-submit-button"
              >
                Upload
              </CobaltButton>
            }
            secondaryButton={
              <CobaltButton
                variant="secondary"
                onClick={handleClose}
                data-testid="cancel-button"
              >
                Cancel
              </CobaltButton>
            }
          />
        }
      >
        <div
          className={styles.uploaderBody}
          data-testid="upload-approval-letter-body"
        >
          {currentFile !== null ? (
            <>
              {uploadError !== null && (
                <CobaltSectionMessage
                  variant="danger"
                  sectionMessageTitle={
                    <h3 className={styles.uploadErrorTitle}>{uploadError}</h3>
                  }
                  className={styles.uploadErrorMessage}
                  data-testid="upload-error-message"
                />
              )}
              {uploaderLabel}
              <p
                className={`body-01 ${styles.uploaderDescription}`}
                data-testid="uploading-for-description"
              >
                {APPROVAL_LETTER_CONSTRAINTS_DESCRIPTION}
              </p>
              <UploadedFileItem
                fileItem={currentFile}
                onRemove={handleRemoveFile}
              />
            </>
          ) : (
            <CobaltFileUploader
              variant="drop"
              acceptMultipleFiles={false}
              fileExtensions={APPROVAL_LETTER_FILE_EXTENSIONS}
              maxFileSize={APPROVAL_LETTER_MAX_FILE_SIZE_KB}
              label={uploaderLabel}
              description={APPROVAL_LETTER_CONSTRAINTS_DESCRIPTION}
              hasRemoveOnUploadedItem
              files={files}
              onFilesChange={handleFilesChange}
            />
          )}
        </div>
      </CobaltModal>
    );
  }
);

UploadApprovalLetterModal.displayName = 'UploadApprovalLetterModal';
export { UploadApprovalLetterModal };
